import {OligoDuplexVersionGraphQL} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {getWidthForBaseCount} from 'modbio/polymer-maps/OligoMap';
import {AttachedMoleculeDimensionsMap} from 'molbio/maps/linear/AttachedMoleculesUtil';

import {getDisplayResidueIndex} from '../ComplexPolymerUtil';

// The margin space before/after two consecutive elements in the map
export const ELEMENT_MARGIN = 10;
// Horizontal margin on conjugate chips
export const CHIP_XMARGIN = 8;

// The maximum number of bases that a chip can span
export const CONJUGATE_MAX_BASES_SPAN = 4;

export function getChipOverhang(chipDimensions: AttachedMoleculeDimensionsMap, basesLength: number): number {
  let maxChipOverhang = 0;

  const startingResidueIdx = Math.max(0, basesLength - 1 - CONJUGATE_MAX_BASES_SPAN);

  for (let residueIdx = startingResidueIdx; residueIdx < basesLength; residueIdx++) {
    const chipDimensionsAtResidue = chipDimensions[residueIdx] ?? [];
    const chipWidths = chipDimensionsAtResidue.map((dimension) => dimension.width);
    const maxChipWidthAtResidue = Math.max(...chipWidths);
    const chipBasesSpan = basesLength - residueIdx;

    const chipOverhangAtResidue = Math.max(
      maxChipWidthAtResidue - getWidthForBaseCount(chipBasesSpan) - CHIP_XMARGIN,
      0
    );

    maxChipOverhang = Math.max(maxChipOverhang, chipOverhangAtResidue);
  }

  return maxChipOverhang;
}

export function getOligoStrandLeftPadding(offset: number, isForward: boolean): number {
  // The number of "empty" bases after which the strand starts on the polymer map
  // For example, the following structure's forward strand would have a left padding of 2
  // and there reverse strand would have a left padding of 0:
  // --GATTAAA
  // AACTAAT--
  return isForward ? Math.max(offset, 0) : Math.abs(Math.min(offset, 0));
}

export function getOligoDuplexTotalLength(oligoDuplexVersion: OligoDuplexVersionGraphQL): number {
  // The total length of an oligo duplex including any overhangs
  // For example, the following structure would have a length of 9:
  // --GATTAAA
  // AACTAAT--
  const forwardBasesLength = oligoDuplexVersion.forwardStrand?.oligo.bases.length ?? 0;
  const reverseBasesLength = oligoDuplexVersion.reverseStrand?.oligo.bases.length ?? 0;
  const offset = oligoDuplexVersion.offset ?? 0;
  if (offset <= 0) {
    return Math.max(forwardBasesLength, reverseBasesLength + Math.abs(offset));
  } else {
    return Math.max(reverseBasesLength, forwardBasesLength + Math.abs(offset));
  }
}

export function getReverseAdjustedDisplayResidueIdx(
  idx: number,
  sequenceLength: number,
  isForward: boolean
): number {
  // Oligo duplexes are stored with both the forward and reverse strand oriented left to right
  // as they would be read on the polymer map. When displaying a residue index, we must invert the indices
  // of the bases in the reverse strand as they would be if the strand was read right to left.
  // For example, in the following structure, the indices of both strands would be stored as
  //     0 1 2 3
  // - - G A T T
  // A A C T - -
  // 0 1 2 3
  // And this is then converted to
  //     1 2 3 4
  // - - G A T T
  // A A C T - -
  // 4 3 2 1
  const residueIdx = isForward ? idx : sequenceLength - idx - 1;
  return getDisplayResidueIndex(residueIdx);
}
